import { useEffect } from "react";

import { useModalScrollLock } from "../../hooks/use-modal-scroll-lock";
import { useLang } from "../../lib/i18n";
import { appCopy } from "../../lib/i18n/app";
import { IconBarcode, IconCamera, IconKeyboard, IconList, IconMic, IconStar } from "./icon";
import { Eyebrow } from "./primitives";

type Props = {
  open: boolean;
  onClose: () => void;
  onText: () => void;
  onPhoto: () => void;
  onVoice: () => void;
  onBarcode: () => void;
  onMenu: () => void;
  onFavorites: () => void;
};

const METHOD_ICONS = [
  { id: "text", Icon: IconKeyboard },
  { id: "photo", Icon: IconCamera },
  { id: "voice", Icon: IconMic },
  { id: "barcode", Icon: IconBarcode },
  { id: "menu", Icon: IconList },
];

export function AddSheet({
  open,
  onClose,
  onText,
  onPhoto,
  onVoice,
  onBarcode,
  onMenu,
  onFavorites,
}: Props) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const METHOD_TEXT: Record<string, { label: string; sub: string }> = {
    text: { label: t.addMethodTextLabel, sub: t.addMethodTextSub },
    photo: { label: t.addMethodPhotoLabel, sub: t.addMethodPhotoSub },
    voice: { label: t.addMethodVoiceLabel, sub: t.addMethodVoiceSub },
    barcode: { label: t.addMethodBarcodeLabel, sub: t.addMethodBarcodeSub },
    menu: { label: t.addMethodMenuLabel, sub: t.addMethodMenuSub },
  };
  const METHODS = METHOD_ICONS.map((m) => ({ ...m, ...METHOD_TEXT[m.id] }));
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);
  // Lock body scroll while the sheet is open — this overlay is a plain
  // <div className="fixed inset-0">, not a native <dialog>.showModal(), so
  // iOS WKWebView will happily momentum-scroll the page underneath
  // (gotchas handoff §4.1). The hook is a no-op when open is false.
  useModalScrollLock(open);

  if (!open) return null;

  const handlers: Record<string, () => void> = {
    text: onText,
    photo: onPhoto,
    voice: onVoice,
    barcode: onBarcode,
    menu: onMenu,
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <button
        type="button"
        onClick={onClose}
        aria-label={t.closeAria}
        className="absolute inset-0"
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          WebkitBackdropFilter: "blur(4px)",
        }}
      />
      <div
        className="relative mx-auto w-full"
        style={{
          maxWidth: 480,
          background: "var(--bg)",
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          padding: "20px 22px max(28px, env(safe-area-inset-bottom))",
          boxShadow: "var(--shadow-sheet)",
          animation: "addsheet-in 220ms cubic-bezier(0.2, 0.7, 0.3, 1)",
        }}
      >
        <div
          aria-hidden
          className="mx-auto mb-3"
          style={{
            width: 40,
            height: 4,
            borderRadius: 99,
            background: "var(--track)",
          }}
        />
        <Eyebrow>{t.addSheetTitle}</Eyebrow>
        <div className="mt-3 grid grid-cols-2 gap-2">
          {METHODS.map((m) => {
            const { Icon } = m;
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  handlers[m.id]?.();
                  onClose();
                }}
                className="flex items-center gap-3 px-4 py-4 text-left transition-colors"
                style={{
                  background: "var(--surface)",
                  borderRadius: 14,
                  boxShadow: "0 0 0 1px var(--nav-border)",
                  color: "var(--ink)",
                }}
              >
                <span
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: "var(--surface-2)",
                    color: "var(--ink)",
                  }}
                >
                  <Icon size={20} />
                </span>
                <span className="flex flex-col">
                  <span className="text-[14px] font-semibold leading-tight">{m.label}</span>
                  <span
                    className="text-[10px] tracking-[0.04em] mt-0.5"
                    style={{ fontFamily: "var(--font-mono)", color: "var(--muted)" }}
                  >
                    {m.sub}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            onFavorites();
            onClose();
          }}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3.5 text-[13px] font-semibold"
          style={{
            background: "transparent",
            color: "var(--accent)",
            borderRadius: 12,
            boxShadow: "inset 0 0 0 1px var(--nav-border)",
          }}
        >
          <IconStar size={14} filled />
          {t.addFromFavorites}
        </button>
      </div>
      <style>{`
        @keyframes addsheet-in {
          from { transform: translateY(100%); opacity: 0.5; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
